import {
  Button,
  FormControl,
  HStack,
  Input,
  useToast,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import React, { useRef } from 'react';
import { authenticatedHttpRequest } from 'utils/http';

export interface ImageURLInputProps {
  onNewSourceUrl(newUrl: string | undefined): void;
}

export const ImageURLInput = ({ onNewSourceUrl }: ImageURLInputProps) => {
  const urlInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const onError = () => {
    toast({
      status: 'error',
      title: t`Unable to load image`,
      description: t`Try using a different image or try again later.`,
    });
  };

  return (
    <FormControl>
      <HStack>
        <Input type="url" placeholder={t`Image link`} ref={urlInputRef} />
        <Button
          aria-label={t`Preview image`}
          onClick={() => {
            if (!urlInputRef.current?.value) {
              return;
            }
            proxyUnsafeUrl(urlInputRef.current.value, onNewSourceUrl, onError);
          }}
        >{t`Preview`}</Button>
      </HStack>
    </FormControl>
  );
};

interface SafeURLServiceResponse {
  safeUrl: string;
}

function isSafeUrlServiceResponse(
  response: any,
): response is SafeURLServiceResponse {
  return response && typeof response.safeUrl === 'string';
}

function proxyUnsafeUrl(
  unsafeUrl: string,
  onProxyAvailable: (proxyUrl: string) => void,
  onError: () => void,
): void {
  if (unsafeUrl && unsafeUrl.startsWith('data:')) {
    onProxyAvailable(unsafeUrl);
  } else {
    authenticatedHttpRequest(
      '/_safe_image_url?' +
        new URLSearchParams({
          link: unsafeUrl,
        }),
      {
        method: 'GET',
      },
    )
      .then((response) => {
        response.json().then((serviceResponse: SafeURLServiceResponse) => {
          if (isSafeUrlServiceResponse(serviceResponse)) {
            onProxyAvailable(serviceResponse.safeUrl);
          } else {
            throw new Error('Unexpected response from safe url service');
          }
        });
      })
      .catch((e) => {
        onError();
      });
  }
}
